import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider
} from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';
import InfoIcon from '@mui/icons-material/Info';
import SecurityIcon from '@mui/icons-material/Security';
import { useTranslation } from 'react-i18next';

const CryptoComplianceBanner = ({ onAccept, onDecline, show = true }) => {
  const { t } = useTranslation();
  const [showFullDisclaimer, setShowFullDisclaimer] = useState(false);

  if (!show) return null;

  return (
    <>
      <Alert 
        severity="warning" 
        sx={{ 
          mb: 2,
          direction: 'ltr', // Force LTR layout for this component
          '& .MuiAlert-action': {
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 1,
            alignItems: 'stretch',
            minWidth: { xs: 'auto', sm: '200px' } // Ensure minimum width for buttons
          },
          '& .MuiAlert-message': {
            minWidth: 0, // Allow text to wrap properly
            flex: 1
          }
        }}
        action={
          <Box sx={{ 
            display: 'flex', 
            gap: 1, 
            flexDirection: { xs: 'column', sm: 'row' },
            width: { xs: '100%', sm: 'auto' },
            flexShrink: 0 // Prevent buttons from shrinking
          }}>
            <Button 
              color="inherit" 
              size="small" 
              onClick={onDecline}
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Decline
            </Button>
            <Button 
              color="inherit" 
              size="small" 
              onClick={onAccept}
              variant="outlined"
              sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
            >
              Accept & Continue
            </Button>
          </Box>
        }
      >
        <AlertTitle sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
          ⚠️ Cryptocurrency Risk Warning
        </AlertTitle>
        <Typography 
          variant="body2" 
          sx={{ 
            fontSize: { xs: '0.875rem', sm: '0.875rem' },
            lineHeight: 1.5,
            wordBreak: 'break-word',
            textAlign: 'left' // Force left alignment
          }}
        >
          Tulidu is a read-only portfolio tracking tool. We do not allow users to buy, sell, trade, or transfer cryptocurrency. We only track your existing balances. Cryptocurrency investments carry significant risk. You may lose some or all of your investment.
        </Typography>
      </Alert>

      {/* Full Disclaimer Dialog */}
      <Dialog 
        open={showFullDisclaimer} 
        onClose={() => setShowFullDisclaimer(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <WarningIcon color="warning" />
            Cryptocurrency Investment Risk Disclaimer
          </Box>
        </DialogTitle>
        <DialogContent>
            <Typography variant="body2" paragraph>
              <strong>IMPORTANT:</strong> Please read this disclaimer carefully before using Tulidu. 
              Tulidu is a read-only portfolio tracking tool. We do not allow users to buy, sell, trade, or transfer cryptocurrency. 
              By using our service, you acknowledge that you understand and accept the risks associated with cryptocurrency investments.
            </Typography>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SecurityIcon color="primary" />
              What Tulidu Is and Is Not
            </Typography>
            <List dense>
              <ListItem>
                <ListItemIcon>
                  <InfoIcon color="success" />
                </ListItemIcon>
                <ListItemText 
                  primary="✅ Read-Only Portfolio Tracking Tool" 
                  secondary="Tulidu only tracks and displays your existing cryptocurrency balances"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="❌ NOT a Financial Institution" 
                  secondary="We are not a bank, securities firm, or licensed financial institution"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="❌ NOT an Investment Advisor" 
                  secondary="We do not provide investment advice, recommendations, or financial guidance"
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <WarningIcon color="error" />
                </ListItemIcon>
                <ListItemText 
                  primary="❌ NOT a Trading Platform" 
                  secondary="We do not execute trades, hold funds, manage investments, or allow buying/selling"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <WarningIcon color="warning" />
              Cryptocurrency Investment Risks
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="High Volatility" 
                  secondary="Cryptocurrency values can fluctuate dramatically in short periods"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Market Risk" 
                  secondary="Cryptocurrency markets are highly speculative and unpredictable"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Regulatory Risk" 
                  secondary="Government regulations may impact cryptocurrency values and availability"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Technology Risk" 
                  secondary="Technical issues, hacking, or network problems may affect your investments"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Liquidity Risk" 
                  secondary="You may not be able to sell your cryptocurrencies when desired"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="Total Loss Risk" 
                  secondary="You may lose some or all of your invested capital"
                />
              </ListItem>
            </List>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Your Responsibilities
            </Typography>
            <Typography variant="body2" paragraph>
              By using Tulidu, you acknowledge and agree that:
            </Typography>
            <List dense>
              <ListItem>
                <ListItemText 
                  primary="• You are solely responsible for your investment decisions"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• You understand the risks associated with cryptocurrency investments"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• You will conduct your own research before making investment decisions"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• You will consult with qualified financial advisors if needed"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• You will only invest what you can afford to lose"
                />
              </ListItem>
              <ListItem>
                <ListItemText 
                  primary="• You understand that past performance does not guarantee future results"
                />
              </ListItem>
            </List>
          </Box>

          <Alert severity="error" sx={{ mt: 2 }}>
            <AlertTitle>Final Warning</AlertTitle>
            <Typography variant="body2">
              <strong>Cryptocurrency investments are not suitable for all investors.</strong> 
              Only invest money you can afford to lose completely. If you are unsure about 
              cryptocurrency investments, please consult with a qualified financial advisor.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowFullDisclaimer(false)}>
            Close
          </Button>
          <Button 
            onClick={() => {
              setShowFullDisclaimer(false);
              onAccept();
            }}
            variant="contained"
            color="primary"
          >
            I Understand and Accept
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CryptoComplianceBanner;
